/**
 * ReportContentModal Component
 *
 * Modal dialog for reporting inappropriate content.
 * Users can select a reason and add optional details.
 */

import { useState, useCallback, type CSSProperties } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import FlagIcon from '@mui/icons-material/Flag';
import { useContentFlag } from '@/hooks/useContentFlag';
import { FLAG_REASON_LABELS, type FlagContentType, type FlagReason } from '@flash/shared';

/**
 * Props for ReportContentModal component
 */
interface ReportContentModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Called when modal is closed */
  onClose: () => void;
  /** Type of content being reported */
  contentType: FlagContentType;
  /** ID of the content being reported */
  contentId: string;
  /** Gym ID the content belongs to */
  gymId: string;
  /** Optional preview of the content being reported */
  contentPreview?: string;
  /** Called after successful submission */
  onSuccess?: () => void;
}

/**
 * ReportContentModal component for flagging content
 */
export const ReportContentModal = ({
  isOpen,
  onClose,
  contentType,
  contentId,
  gymId,
  contentPreview,
  onSuccess,
}: ReportContentModalProps) => {
  const { submitFlag, isSubmitting } = useContentFlag();

  // Form state
  const [reason, setReason] = useState<FlagReason | ''>('');
  const [details, setDetails] = useState('');

  // UI state
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  /**
   * Reset form to initial state
   */
  const resetForm = useCallback(() => {
    setReason('');
    setDetails('');
    setError(null);
    setSuccess(false);
  }, []);

  /**
   * Handle modal close
   */
  const handleClose = useCallback(() => {
    if (isSubmitting) return;
    resetForm();
    onClose();
  }, [isSubmitting, resetForm, onClose]);

  /**
   * Handle backdrop click
   */
  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      handleClose();
    }
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!reason) {
      setError('Please select a reason for reporting');
      return;
    }

    setError(null);

    try {
      const trimmedDetails = details.trim();
      await submitFlag({
        contentType,
        contentId,
        gymId,
        reason,
        ...(trimmedDetails ? { details: trimmedDetails } : {}),
      });

      setSuccess(true);

      // Call success callback after a brief delay to show success message
      setTimeout(() => {
        resetForm();
        onSuccess?.();
        onClose();
      }, 1500);
    } catch (submitError) {
      const errorMessage =
        submitError instanceof Error ? submitError.message : 'Failed to submit report';
      console.error('Error submitting report:', submitError);
      setError(errorMessage);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      style={overlayStyle}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="report-modal-title"
    >
      <div style={modalStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <div style={headerTitleContainerStyle}>
            <FlagIcon style={{ color: 'var(--color-error)', marginRight: '8px' }} />
            <h2 id="report-modal-title" style={titleStyle}>
              Report Content
            </h2>
          </div>
          <button
            type="button"
            onClick={handleClose}
            style={closeButtonStyle}
            aria-label="Close"
            disabled={isSubmitting}
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>

        {/* Success Message */}
        {success ? (
          <div style={successContainerStyle}>
            <div style={successIconStyle}>
              <CheckCircleOutlineIcon style={{ fontSize: 48, color: 'var(--color-success)' }} />
            </div>
            <h3 style={successTitleStyle}>Report Submitted</h3>
            <p style={successMessageStyle}>
              Thank you for helping keep our community safe. A moderator will review this content.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {/* Content */}
            <div style={contentStyle}>
              {/* Content Preview */}
              {contentPreview && (
                <div style={previewContainerStyle}>
                  <p style={previewLabelStyle}>Reporting this {contentType}:</p>
                  <div style={previewTextStyle}>
                    {contentPreview.length > 150
                      ? `${contentPreview.substring(0, 150)}...`
                      : contentPreview}
                  </div>
                </div>
              )}

              {/* Reason (required) */}
              <div style={fieldStyle}>
                <label style={labelStyle}>
                  Why are you reporting this? <span style={requiredStyle}>*</span>
                </label>
                <div style={radioGroupStyle}>
                  {(Object.keys(FLAG_REASON_LABELS) as FlagReason[]).map((flagReason) => (
                    <label key={flagReason} style={radioLabelStyle}>
                      <input
                        type="radio"
                        name="reason"
                        value={flagReason}
                        checked={reason === flagReason}
                        onChange={() => setReason(flagReason)}
                        style={radioInputStyle}
                        disabled={isSubmitting}
                      />
                      <span style={radioTextStyle}>{FLAG_REASON_LABELS[flagReason]}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Additional Details (optional) */}
              <div style={fieldStyle}>
                <label htmlFor="report-details" style={labelStyle}>
                  Additional details (optional)
                </label>
                <textarea
                  id="report-details"
                  value={details}
                  onChange={(e) => setDetails(e.target.value)}
                  placeholder="Provide any additional context that might help our moderators..."
                  style={textareaStyle}
                  rows={3}
                  maxLength={500}
                  disabled={isSubmitting}
                />
                <div style={charCountStyle}>{details.length}/500</div>
              </div>

              {/* Error Message */}
              {error && <div style={errorStyle}>{error}</div>}
            </div>

            {/* Actions */}
            <div style={actionsStyle}>
              <button
                type="button"
                onClick={handleClose}
                style={cancelButtonStyle}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                style={{
                  ...submitButtonStyle,
                  ...(isSubmitting || !reason ? disabledButtonStyle : {}),
                }}
                disabled={isSubmitting || !reason}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Report'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

/**
 * Styles
 */
const overlayStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '20px',
  zIndex: 1000,
};

const modalStyle: CSSProperties = {
  backgroundColor: 'var(--color-bg-primary)',
  borderRadius: '12px',
  maxWidth: '440px',
  width: '100%',
  maxHeight: '90vh',
  overflow: 'auto',
  boxShadow: 'var(--shadow-lg)',
};

const headerStyle: CSSProperties = {
  padding: '20px 24px',
  borderBottom: '1px solid var(--color-border)',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const headerTitleContainerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
};

const titleStyle: CSSProperties = {
  fontSize: '1.125rem',
  fontWeight: 700,
  color: 'var(--color-text-primary)',
  margin: 0,
};

const closeButtonStyle: CSSProperties = {
  padding: '8px',
  backgroundColor: 'transparent',
  border: 'none',
  borderRadius: '6px',
  color: 'var(--color-text-secondary)',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const contentStyle: CSSProperties = {
  padding: '20px 24px',
};

const previewContainerStyle: CSSProperties = {
  marginBottom: '20px',
  padding: '12px',
  backgroundColor: 'var(--color-bg-secondary)',
  borderRadius: '8px',
  border: '1px solid var(--color-border)',
};

const previewLabelStyle: CSSProperties = {
  fontSize: '0.75rem',
  fontWeight: 500,
  color: 'var(--color-text-secondary)',
  marginBottom: '8px',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

const previewTextStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--color-text-primary)',
  lineHeight: 1.5,
  wordBreak: 'break-word',
};

const fieldStyle: CSSProperties = {
  marginBottom: '20px',
};

const labelStyle: CSSProperties = {
  display: 'block',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'var(--color-text-primary)',
  marginBottom: '12px',
};

const requiredStyle: CSSProperties = {
  color: 'var(--color-error)',
};

const radioGroupStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
};

const radioLabelStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  cursor: 'pointer',
  padding: '10px 12px',
  backgroundColor: 'var(--color-bg-secondary)',
  borderRadius: '8px',
  border: '1px solid var(--color-border)',
  transition: 'border-color 0.15s',
};

const radioInputStyle: CSSProperties = {
  flexShrink: 0,
};

const radioTextStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--color-text-primary)',
};

const textareaStyle: CSSProperties = {
  width: '100%',
  padding: '12px',
  fontSize: '0.875rem',
  border: '1px solid var(--color-border-hover)',
  borderRadius: '8px',
  resize: 'vertical',
  minHeight: '80px',
  boxSizing: 'border-box',
  backgroundColor: 'var(--color-bg-primary)',
  color: 'var(--color-text-primary)',
};

const charCountStyle: CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--color-text-muted)',
  textAlign: 'right',
  marginTop: '4px',
};

const errorStyle: CSSProperties = {
  padding: '12px',
  borderRadius: '8px',
  backgroundColor: 'var(--color-bg-secondary)',
  border: '1px solid var(--color-error)',
  color: 'var(--color-error)',
  fontSize: '0.875rem',
};

const actionsStyle: CSSProperties = {
  padding: '16px 24px',
  borderTop: '1px solid var(--color-border)',
  display: 'flex',
  gap: '12px',
  justifyContent: 'flex-end',
};

const cancelButtonStyle: CSSProperties = {
  padding: '10px 20px',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'var(--color-text-primary)',
  backgroundColor: 'var(--color-bg-primary)',
  border: '1px solid var(--color-border-hover)',
  borderRadius: '8px',
  cursor: 'pointer',
};

const submitButtonStyle: CSSProperties = {
  padding: '10px 20px',
  fontSize: '0.875rem',
  fontWeight: 600,
  color: 'white',
  backgroundColor: 'var(--color-error)',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
};

const disabledButtonStyle: CSSProperties = {
  backgroundColor: 'var(--color-text-muted)',
  cursor: 'not-allowed',
};

const successContainerStyle: CSSProperties = {
  padding: '40px 24px',
  textAlign: 'center',
};

const successIconStyle: CSSProperties = {
  marginBottom: '16px',
};

const successTitleStyle: CSSProperties = {
  fontSize: '1.125rem',
  fontWeight: 600,
  color: 'var(--color-text-primary)',
  marginBottom: '8px',
};

const successMessageStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--color-text-secondary)',
  lineHeight: 1.5,
};

export default ReportContentModal;
